/**
 * Mating plans. Pick the females, say what you want in a sire, see who fits and why (EPD targets,
 * relationship, semen on hand), then assign. A plan stays a plan until the AI or exposure is recorded.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { localDate, type JobDetail, type JobItemDTO, type JobSummary } from "../../../contract";
import { RefPicker } from "../../../web/forms/pickers";
import { ApiError, api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan, useMe } from "../../../web/lib/session";
import { registry } from "../../../web/registry";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, Icon, IconButton, List, Loading, Row, Section, Select, TextInput, cx, toast } from "../../../web/ui/kit";
import { associationShort, formatEpd, type Target } from "../shared/epd";
import { percent } from "../shared/pedigree";
import { MATING_PRESET_LABELS, RISK_LABELS } from "../shared/labels";
import type { MatingAssignment, MatingPlanDTO, PairCheck, ShortlistDTO, SireCandidate } from "../shared/types";
import { GeneticsShell } from "./GeneticsWorkspace";
import { StageSteps, openPrefilled, traitName, useTraits } from "./ui";

const PRESETS: Array<{ id: keyof typeof MATING_PRESET_LABELS; targets: Target[] }> = [
  { id: "heifers", targets: [{ trait: "CED", min: 10 }, { trait: "BW", max: 0 }] },
  { id: "growth", targets: [{ trait: "WW", min: 60 }, { trait: "YW", min: 100 }] },
  { id: "maternal", targets: [{ trait: "CEM", min: 6 }, { trait: "STAY", min: 14 }, { trait: "MILK", min: 15, max: 28 }] },
];

export function RiskChip({ check }: { check: PairCheck | null | undefined }) {
  if (!check) return null;
  const tone = check.risk === "high" ? "bad" : check.risk === "watch" ? "warn" : "neutral";
  const label = t(RISK_LABELS[check.risk]);
  return <Chip tone={tone} icon={check.risk === "none_found" ? "info" : "warn"} title={t("{{known}} of {{possible}} ancestors known", { known: check.known, possible: check.possible })}>{check.f > 0 ? `${label} · ${percent(check.f)}` : label}</Chip>;
}

export function RecessiveChips({ check }: { check: PairCheck | null | undefined }) {
  if (!check?.recessive?.length) return null;
  return (
    <>
      {check.recessive.map((r) => (
        <Chip key={r.condition} tone={r.status === "risk" ? "bad" : "warn"} icon="dna">
          {r.status === "risk" ? t("{{condition}}: both carry it", { condition: r.condition }) : t("{{condition}}: one carries it, the other isn't tested", { condition: r.condition })}
        </Chip>
      ))}
    </>
  );
}

export function relationText(check: PairCheck | null | undefined, sireLabel: string): string | null {
  const r = check?.relations[0];
  if (!r || !check) return check && check.known < check.possible ? t("Pedigree {{known}} of {{possible}} known; gaps are unknown, not unrelated", { known: check.known, possible: check.possible }) : null;
  if (r.kind === "same") return t("Same animal");
  if (r.kind === "parent") return t("{{sire}} is her sire", { sire: sireLabel });
  if (r.kind === "ancestor") return t("{{sire}} is in her pedigree, {{count}} generations back", { sire: sireLabel, count: r.female_gens });
  if (r.kind === "offspring" || r.kind === "descendant") return t("She is in {{sire}}'s pedigree", { sire: sireLabel });
  return t("Both trace to {{ancestor}}", { ancestor: r.ancestor_label });
}

function TargetChips({ c }: { c: SireCandidate }) {
  return (
    <div className="genetics-targets">
      {c.targets.map((x) => (
        <Chip key={x.trait} tone={x.outcome === "pass" ? "good" : x.outcome === "fail" ? "warn" : "neutral"} icon={x.outcome === "pass" ? "check" : x.outcome === "fail" ? "minus" : "help"}>
          {x.trait} {x.value !== undefined ? formatEpd(x.value) : t("not reported")}
        </Chip>
      ))}
    </div>
  );
}

function SireCard({ c, females, pairs, onUseAll, used }: { c: SireCandidate; females: ShortlistDTO["females"]; pairs: ShortlistDTO["pairs"]; onUseAll: () => void; used: number }) {
  const close = females.filter((f) => ["watch", "high"].includes(pairs[f.id]?.[c.id]?.risk ?? ""));
  const carriers = females.filter((f) => pairs[f.id]?.[c.id]?.recessive?.some((r) => r.status === "risk"));
  return (
    <Card className={cx("genetics-sire-card", !c.feasible && "is-out")}>
      <div className="genetics-row-head">
        <span className="genetics-row-title"><Link to={`/cattle/${c.id}`}>{c.label}</Link>{c.registration && <span className="muted mono">{c.registration}</span>}</span>
        {c.source === "owned" ? <Chip tone="herd" icon="animal">{t("Our bull")}</Chip> : <Chip tone="genetics" icon="straw">{t("Semen")}</Chip>}
      </div>
      <div className="genetics-row-meta">
        {c.snapshot ? <span>{associationShort(c.snapshot.association)} · {c.snapshot.release ?? (c.snapshot.as_of ? formatDate(c.snapshot.as_of) : "")}</span> : <span>{t("No EPDs from this association")}</span>}
        {c.semen && <span>{plural(c.semen.available, "{{count}} straw free", "{{count}} straws free")}{c.semen.reserved ? ` · ${plural(c.semen.reserved, "{{count}} held for plans", "{{count}} held for plans")}` : ""}</span>}
      </div>
      {c.targets.length > 0 && <TargetChips c={c} />}
      {close.length > 0 && <Chip tone="warn" icon="warn">{plural(close.length, "Closely related to {{count}} female", "Closely related to {{count}} females")}</Chip>}
      {carriers.length > 0 && <Chip tone="bad" icon="dna">{plural(carriers.length, "Shares a carried condition with {{count}} female", "Shares a carried condition with {{count}} females")}</Chip>}
      {!c.feasible && <p className="muted">{c.why_not === "no_semen" ? t("No straws free") : t("Not in the herd and no semen on hand")}</p>}
      <div className="row-actions">
        <Button size="sm" variant={used ? "secondary" : "primary"} disabled={!c.feasible} onClick={onUseAll}>{t("Use on the rest")}</Button>
        {used > 0 && <span className="muted">{plural(used, "On {{count}} female", "On {{count}} females")}</span>}
      </div>
    </Card>
  );
}

export function MatingList() {
  useLanguage();
  const can = useCan();
  const q = useApi<{ plans: JobSummary[] }>("/m/genetics/mating");
  return (
    <GeneticsShell title={t("Mating plans")} subtitle={t("Who goes on whom. Nothing is bred until the AI or exposure is recorded.")} actions={can("record") && <Button variant="primary" icon="plus" to="/work/genetics/mating/new">{t("New plan")}</Button>}>
      {q.isLoading ? <Loading /> : !q.data?.plans.length ? <Empty icon="mating" title={t("No mating plans yet")} body={t("Start one for the heifers or the cows you'll AI.")} action={can("record") && <Button variant="primary" to="/work/genetics/mating/new">{t("New plan")}</Button>} /> : (
        <List>{q.data.plans.map((p) => <Row key={p.id} icon="mating" tone="genetics" to={`/work/jobs/${p.id}`} title={p.title} meta={[p.status === "done" ? t("Finished") : null, plural(p.progress.total, "{{count}} female", "{{count}} females"), p.progress.done ? plural(p.progress.done, "{{count}} bred", "{{count}} bred") : null].filter(Boolean).join(" · ")} />)}</List>
      )}
    </GeneticsShell>
  );
}

function TargetsEditor({ targets, onChange }: { targets: Target[]; onChange: (t: Target[]) => void }) {
  const traits = useTraits();
  const num = (v: string) => (v.trim() === "" || Number.isNaN(Number(v)) ? undefined : Number(v));
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="choice-chips">{PRESETS.map((p) => <button key={p.id} type="button" className="choice" onClick={() => onChange(p.targets)}>{t(MATING_PRESET_LABELS[p.id])}</button>)}</div>
      {targets.map((x, i) => (
        <div key={i} className="genetics-target-row">
          <div>
            <TextInput aria-label={t("Trait")} className="mono" list="genetics-mating-traits" value={x.trait} onChange={(e) => onChange(targets.map((y, j) => (j === i ? { ...y, trait: e.target.value.trim() } : y)))} />
            <span className="field-hint">{x.trait ? traitName(x.trait, traits.data?.traits) : ""}</span>
          </div>
          <TextInput aria-label={t("At least")} inputMode="decimal" placeholder={t("At least")} value={x.min ?? ""} onChange={(e) => onChange(targets.map((y, j) => (j === i ? { ...y, min: num(e.target.value) } : y)))} />
          <TextInput aria-label={t("At most")} inputMode="decimal" placeholder={t("At most")} value={x.max ?? ""} onChange={(e) => onChange(targets.map((y, j) => (j === i ? { ...y, max: num(e.target.value) } : y)))} />
          <IconButton icon="delete" label={t("Remove")} onClick={() => onChange(targets.filter((_, j) => j !== i))} />
        </div>
      ))}
      <datalist id="genetics-mating-traits">{(traits.data?.traits ?? []).filter((x) => x.kind === "epd" || x.kind === "index").map((x) => <option key={x.key} value={x.key}>{traitName(x.key, traits.data?.traits)}</option>)}</datalist>
      <div><Button size="sm" icon="plus" onClick={() => onChange([...targets, { trait: "" }])}>{t("Add a target")}</Button></div>
    </div>
  );
}

function pickLot(c: SireCandidate | undefined): string | null {
  return c?.semen?.lots.slice().sort((a, b) => b.on_hand - a.on_hand)[0]?.lot_id ?? null;
}

export function NewMatingPlan() {
  useLanguage();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const year = me?.ranch ? localDate(new Date(), me.ranch.timezone).slice(0, 4) : String(new Date().getFullYear());
  const [group, setGroup] = useState<string | null>(null);
  const [females, setFemales] = useState<string[]>([]);
  const [association, setAssociation] = useState((me?.ranch?.settings.genetics as { association?: string } | undefined)?.association ?? "");
  const [targets, setTargets] = useState<Target[]>(PRESETS[0].targets);
  const [list, setList] = useState<ShortlistDTO | null>(null);
  const [assign, setAssign] = useState<Record<string, MatingAssignment>>({});
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const addGroup = async (id: string | null) => {
    setGroup(id);
    if (!id) return;
    const g = await api.get<{ members: Array<{ id: string; sex: string }> }>(`/groups/${id}`);
    setFemales([...new Set([...females, ...g.members.filter((m) => m.sex === "female").map((m) => m.id)])]);
  };
  const find = async () => {
    setBusy(true);
    try {
      setList(await api.post<ShortlistDTO>("/m/genetics/mating/shortlist", { female_ids: females, targets: targets.filter((x) => x.trait && (x.min !== undefined || x.max !== undefined)), association: association || null }));
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const sireById = useMemo(() => new Map((list?.sires ?? []).map((s) => [s.id, s])), [list]);
  const setFor = (femaleId: string, sireId: string) => {
    const c = sireById.get(sireId);
    setAssign({ ...assign, [femaleId]: { sire_id: sireId || null, ...(c?.semen && c.source !== "owned" ? { lot_id: pickLot(c), straws: 1 } : {}) } });
  };
  const useOnRest = (c: SireCandidate) => {
    const next = { ...assign };
    for (const f of list?.females ?? []) if (!next[f.id]?.sire_id && list!.pairs[f.id]?.[c.id]?.risk !== "high") next[f.id] = { sire_id: c.id, ...(c.semen && c.source !== "owned" ? { lot_id: pickLot(c), straws: 1 } : {}) };
    setAssign(next);
  };
  const save = async () => {
    setBusy(true);
    try {
      const { id } = await api.post<{ id: string }>("/m/genetics/mating", {
        title: title.trim() || t("{{year}} mating plan", { year }), season: year, association: association || null,
        targets: targets.filter((x) => x.trait && (x.min !== undefined || x.max !== undefined)), female_ids: females, assignments: assign,
      });
      await invalidateAll();
      navigate(`/work/jobs/${id}`);
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const feasible = (list?.sires ?? []).filter((s) => s.feasible);
  const assigned = Object.values(assign).filter((a) => a.sire_id).length;
  return (
    <GeneticsShell title={t("New mating plan")}>
      <Section title={t("1. Which females")} hint={plural(females.length, "{{count}} picked", "{{count}} picked")}>
        <Field label={t("A whole group")} optional><RefPicker target="group" value={group} onChange={(v) => void addGroup(v as string | null)} /></Field>
        <Field label={t("Females")}><RefPicker target="animal" multiple value={females} onChange={(v) => setFemales((v as string[]) ?? [])} filter={{ sex: "female" }} /></Field>
      </Section>
      <Section title={t("2. What you want in a sire")}>
        <Field label={t("EPDs from")} hint={t("Compare sires on one association's EPDs.")}><TextInput value={association} onChange={(e) => setAssociation(e.target.value)} placeholder={"Red Angus Association of America"} /></Field>
        <TargetsEditor targets={targets} onChange={setTargets} />
        <div className="form-actions"><Button variant="primary" size="lg" icon="search" busy={busy && !list} disabled={!females.length} onClick={() => void find()}>{t("Find sires")}</Button></div>
      </Section>
      {list && (
        <>
          <Section title={t("3. Sires that fit")} hint={t("Our bulls and sires with semen in the tank, best fit first. Relationships use {{depth}} generations of pedigree.", { depth: list.depth })}>
            {!list.sires.length ? <Empty icon="straw" title={t("No bulls or semen on hand")} body={t("Add semen in Semen and embryos, or record your bulls.")} /> : (
              <div className="cards">{list.sires.map((c) => <SireCard key={c.id} c={c} females={list.females} pairs={list.pairs} used={Object.values(assign).filter((a) => a.sire_id === c.id).length} onUseAll={() => useOnRest(c)} />)}</div>
            )}
          </Section>
          <Section title={t("4. Assign")} count={assigned}>
            <ul className="genetics-rows">
              {list.females.map((f) => {
                const a = assign[f.id];
                const pair = a?.sire_id ? list.pairs[f.id]?.[a.sire_id] : null;
                const sire = a?.sire_id ? sireById.get(a.sire_id) : undefined;
                return (
                  <li key={f.id} className="genetics-row">
                    <div className="genetics-row-head">
                      <span className="genetics-row-title"><Link to={`/cattle/${f.id}`} className="mono">{f.label}</Link></span>
                      <Select aria-label={t("Sire for {{female}}", { female: f.label })} value={a?.sire_id ?? ""} onChange={(e) => setFor(f.id, e.target.value)} style={{ width: "auto", minWidth: 160 }}>
                        <option value="">{t("Not yet")}</option>
                        {feasible.map((c) => <option key={c.id} value={c.id}>{c.label}{list.pairs[f.id]?.[c.id]?.risk === "high" ? ` (${t("too close")})` : ""}</option>)}
                      </Select>
                    </div>
                    {pair && <div className="genetics-row-meta"><RiskChip check={pair} /><RecessiveChips check={pair} /><span>{relationText(pair, sire?.label ?? "")}</span></div>}
                  </li>
                );
              })}
            </ul>
          </Section>
          <Section title={t("5. Save the plan")}>
            <Field label={t("Name")} optional><TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("{{year}} mating plan", { year })} /></Field>
            <div className="form-actions"><Button variant="primary" size="lg" icon="check" busy={busy} disabled={!females.length} onClick={() => void save()}>{t("Save plan")}</Button><span className="muted">{t("Saving reserves straws. It doesn't record any breeding.")}</span></div>
          </Section>
        </>
      )}
    </GeneticsShell>
  );
}

/** Job page for a mating plan: planned vs. recorded, semen needed vs. on hand, and changes kept as versions. */
export function MatingPlanJob({ job, refresh }: { job: JobDetail; items: JobItemDTO[]; refresh(): void }) {
  useLanguage();
  const can = useCan();
  const traits = useTraits();
  const q = useApi<MatingPlanDTO>(`/m/genetics/mating/${job.id}`);
  const [changing, setChanging] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const plan = q.data;
  const aiType = registry.event("breeding.ai") ? "breeding.ai" : undefined;
  const sireOptions = plan.sires;
  const update = async (femaleId: string, a: MatingAssignment | null, note?: string) => {
    setBusy(true);
    try {
      await api.put(`/m/genetics/mating/${job.id}`, { assignments: { [femaleId]: a }, note });
      await q.refetch();
      refresh();
      setChanging(null);
    } catch (err) { toast(err instanceof ApiError ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); } finally { setBusy(false); }
  };
  const setStage = async (stage: string) => { await api.put(`/m/genetics/mating/${job.id}`, { assignments: {}, stage }); await invalidateAll(); refresh(); void q.refetch(); };
  const remaining = plan.rows.filter((r) => !r.actual).length;
  return (
    <div className="stack">
      <StageSteps stages={["planning", "ready", "breeding"]} current={job.stage} status={job.status} />
      {plan.targets.length > 0 && (
        <div className="genetics-targets">
          {plan.association && <Chip tone="neutral">{associationShort(plan.association)}</Chip>}
          {plan.targets.map((x) => <Chip key={x.trait} tone="genetics" title={traitName(x.trait, traits.data?.traits)}>{x.trait} {x.min !== undefined ? `≥ ${x.min}` : ""}{x.min !== undefined && x.max !== undefined ? " · " : ""}{x.max !== undefined ? `≤ ${x.max}` : ""}</Chip>)}
        </div>
      )}
      {can("record") && job.status !== "done" && (
        <div className="row-actions">
          {job.stage === "planning" && <Button variant="primary" onClick={() => void setStage("ready")}>{t("Ready to breed")}</Button>}
          {job.stage === "ready" && <Button variant="primary" onClick={() => void setStage("breeding")}>{t("Breeding has started")}</Button>}
          <span className="muted">{plural(remaining, "{{count}} female not bred yet", "{{count}} females not bred yet")}</span>
        </div>
      )}
      <Section title={t("Sires")}>
        <List>
          {plan.sires.map((s) => (
            <Row key={s.id} icon={s.semen ? "straw" : "animal"} tone="genetics" to={`/cattle/${s.id}`} title={s.label}
              meta={[plural(s.planned, "Planned on {{count}}", "Planned on {{count}}"), s.bred ? plural(s.bred, "{{count}} bred", "{{count}} bred") : null, s.semen ? plural(s.semen.on_hand, "{{count}} straw on hand", "{{count}} straws on hand") : null].filter(Boolean).join(" · ")}
              trailing={s.short > 0 ? <Chip tone="warn" icon="warn">{plural(s.short, "{{count}} straw short", "{{count}} straws short")}</Chip> : undefined} />
          ))}
        </List>
      </Section>
      <Section title={t("Females")} count={plan.rows.length}>
        <ul className="genetics-rows">
          {plan.rows.map((r) => {
            const sireLabel = plan.sires.find((s) => s.id === r.assignment?.sire_id)?.label ?? "";
            return (
              <li key={r.item_id} className={cx("genetics-row", r.actual && "is-skipped")}>
                <div className="genetics-row-head">
                  <span className="genetics-row-title"><Link to={`/cattle/${r.female.id}`} className="mono">{r.female.label}</Link>{sireLabel ? <span>→ {sireLabel}</span> : <span className="muted">{t("No sire yet")}</span>}</span>
                  {r.actual ? (
                    <Chip tone={r.actual.matches === false ? "warn" : "good"} icon={r.actual.matches === false ? "warn" : "done"}>
                      {r.actual.sire_label ? t("Bred to {{sire}} {{date}}", { sire: r.actual.sire_label, date: r.actual.on ? formatDate(r.actual.on) : "" }) : t("Bred {{date}}", { date: r.actual.on ? formatDate(r.actual.on) : "" })}
                    </Chip>
                  ) : can("record") && (
                    <div className="genetics-row-actions">
                      {aiType && r.assignment?.sire_id && <Button size="sm" variant="primary" onClick={() => openPrefilled(aiType, [{ kind: "animal", id: r.female.id, role: "animal", label: r.female.label }], { sire_id: r.assignment!.sire_id, lot_id: r.assignment!.lot_id, semen_lot_id: r.assignment!.lot_id }, { job_id: job.id, onDone: () => void q.refetch() })}>{t("Record AI")}</Button>}
                      <Button size="sm" variant="ghost" icon="edit" onClick={() => setChanging(changing === r.item_id ? null : r.item_id)}>{t("Change")}</Button>
                    </div>
                  )}
                </div>
                <div className="genetics-row-meta">
                  {r.check && <RiskChip check={r.check} />}
                  <RecessiveChips check={r.check} />
                  {r.check && <span>{relationText(r.check, sireLabel)}</span>}
                  {r.assignment?.note && <span>{r.assignment.note}</span>}
                </div>
                {changing === r.item_id && (
                  <ChangeSire options={sireOptions} current={r.assignment} busy={busy} onSave={(a, note) => void update(r.female.id, a, note)} />
                )}
              </li>
            );
          })}
        </ul>
      </Section>
      {plan.versions.length > 0 && (
        <Disclosure label={t("Earlier versions")} count={plan.versions.length}>
          <List>{plan.versions.slice().reverse().map((v, i) => <Row key={i} icon="history" title={formatDate(v.at)} meta={[plural(v.assigned, "{{count}} assigned", "{{count}} assigned"), v.note].filter(Boolean).join(" · ")} />)}</List>
        </Disclosure>
      )}
    </div>
  );
}

function ChangeSire({ options, current, busy, onSave }: { options: MatingPlanDTO["sires"]; current: MatingAssignment | null; busy: boolean; onSave: (a: MatingAssignment | null, note?: string) => void }) {
  const [sire, setSire] = useState<string | null>(current?.sire_id ?? null);
  const [other, setOther] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const chosen = other ?? sire;
  const opt = options.find((o) => o.id === chosen);
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div className="choice-chips">
        {options.map((o) => <button key={o.id} type="button" className={cx("choice", !other && sire === o.id && "choice-on")} onClick={() => { setOther(null); setSire(o.id); }}>{o.label}</button>)}
      </div>
      <Field label={t("Another sire")} optional><RefPicker target="animal" value={other} onChange={(v) => setOther(v as string | null)} filter={{ sex: "male", status: "all" }} /></Field>
      <Field label={t("Why the change?")} optional><TextInput value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      <div className="row-actions">
        <Button variant="primary" busy={busy} disabled={!chosen} onClick={() => onSave({ sire_id: chosen, ...(opt?.semen?.lots[0] ? { lot_id: opt.semen.lots[0].lot_id, straws: 1 } : {}) }, note || undefined)}>{t("Save")}</Button>
        <Button variant="ghost" busy={busy} onClick={() => onSave(null, note || undefined)}>{t("Clear")}</Button>
      </div>
      {!opt && other && <p className="muted"><Icon name="info" size={14} /> {t("Semen for this sire will be picked when you record the AI.")}</p>}
    </div>
  );
}
